import { Injectable } from '@angular/core';
import { NavigationCancel, NavigationEnd } from '@angular/router';
import { AnalyticsService } from '@dxo/ng-swift-ui';
import { Observable, Subscription } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class PageTurnService {
  constructor(private eventTrackingService: AnalyticsService) {}

  private navigationStartTime = 0;
  private navigationEventStack = 0;
  private delayStopStartTime = 0;
  private delayStopSubscription: Subscription | undefined;
  private pageName: string | undefined;
  private sessionStorageStartTimeKey = 'crossAppNavEventStartTime';
  private redirecting = false;

  startNavigation() {
    if (this.redirecting) {
      this.redirecting = false;
      return;
    }

    this.navigationEventStack++;
    if (this.navigationEventStack === 1) {
      const crossAppNavEventStartTime = sessionStorage.getItem(this.sessionStorageStartTimeKey);
      if (crossAppNavEventStartTime) {
        this.navigationStartTime = Number(crossAppNavEventStartTime);
        sessionStorage.removeItem(this.sessionStorageStartTimeKey);
      } else {
        this.navigationStartTime = new Date().getTime();
      }
    }
  }

  prepareCrossAppPageTurn() {
    this.redirecting = true;
    sessionStorage.setItem(this.sessionStorageStartTimeKey, this.navigationStartTime.toString());
  }

  delayStopNavigation(observable: Observable<unknown>) {
    this.delayStopSubscription = observable.subscribe(this.handleDelayStopCallback.bind(this));
  }

  stopNavigation(event: NavigationCancel | NavigationEnd) {
    if (this.navigationEventStack === 0) {
      console.warn('Navigation events did not balance, page view tracking may be affected');
      return;
    }

    // I wish there was a better way, but when a guard issues a redirect,
    // angular cancels the 'current' navigation _before_ it begins the
    // second navigation. the only way to detect this that I can find is
    // to check the event reason. if we don't handle this case special,
    // then our timings get reset during this type of event and we don't
    // accurately capture the timings.
    if (
      event instanceof NavigationCancel &&
      this.navigationEventStack === 1 &&
      (event.reason?.includes('Redirecting') ?? false)
    ) {
      this.redirecting = true;
      console.warn('Ignoring end of navigation chain due to detected redirection.');
      return;
    }

    this.navigationEventStack--;
    if (this.navigationEventStack === 0) {
      if (event instanceof NavigationCancel) {
        if (!this.redirecting) {
          console.warn('Navigation event cancelled, resetting tracking');
        }

        this.resetTracking();
        return;
      }

      this.pageName = this.getPageName(event);
      if (this.delayStopSubscription) {
        this.delayStopStartTime = new Date().getTime();
        return;
      } else {
        this.trackPageView(this.pageName);
        this.resetTracking();
      }
    }
  }

  private getPageName(event: NavigationEnd): string {
    return event.url.split('?')[0];
  }

  private handleDelayStopCallback() {
    if (this.navigationEventStack !== 0) {
      console.warn(
        'New navigation events began before existing navigation completion ' +
          'callbacks were processed. Page load timing may be inaccurate.'
      );
    }

    // tslint:disable-next-line: no-non-null-assertion
    this.trackPageView(this.pageName!);
    this.resetTracking();
  }

  private trackPageView(pageName: string) {
    const now = new Date().getTime();
    const durationMs = now - this.navigationStartTime;
    const delayedDurationMs = this.delayStopStartTime !== 0 ? now - this.delayStopStartTime : 0;
    const timingData = {
      pageName,
      totalDuration: durationMs,
      navEventDuration: durationMs - delayedDurationMs,
      dataLoadDuration: delayedDurationMs
    };

    this.eventTrackingService.trackPageView({
      page: pageName,
      pageLoadDurationMs: timingData.totalDuration,
      navigationDurationMs: timingData.navEventDuration,
      dataLoadDurationMs: timingData.dataLoadDuration
    });
  }

  private resetTracking() {
    this.navigationStartTime = 0;
    this.navigationEventStack = 0;
    this.delayStopStartTime = 0;
    this.pageName = undefined;
    this.redirecting = false;
    if (this.delayStopSubscription) {
      this.delayStopSubscription.unsubscribe();
      this.delayStopSubscription = undefined;
    }
  }
}
